<template>
    <div>
        <bs5-page-header :title="$t('dashboard.greeting', { name: firstName })" :subtitle="$t('dashboard.subtitle')">
            <template #actions>
                <bs5-base-button variant="secondary" :loading="loading" @click="load">
                    <i v-if="!loading" class="bi bi-arrow-clockwise"></i>{{ $t("common.refresh") }}
                </bs5-base-button>
            </template>
        </bs5-page-header>

        <div class="au-stats">
            <bs5-stat-card icon="bi-briefcase-fill" tone="indigo" :value="clientsCount" :loading="loading"
                :label="$t('dashboard.clients')" />
            <bs5-stat-card icon="bi-shop" tone="cyan" :value="distributorsCount" :loading="loading"
                :label="$t('dashboard.distributors')" />
            <bs5-stat-card v-if="isSuperAdmin" icon="bi-people-fill" tone="rose" :value="usersCount"
                :loading="loading" :label="$t('dashboard.users')" />
            <bs5-stat-card icon="bi-shield-check" tone="amber" :value="roleName" is-text
                :label="$t('dashboard.role')" />
        </div>

        <h2 class="au-card-title mb-3">{{ $t("dashboard.shortcuts") }}</h2>
        <div class="au-stats">
            <router-link class="au-card au-shortcut" :to="{ name: 'Clients' }">
                <div class="au-tile is-indigo"><i class="bi bi-briefcase-fill"></i></div>
                <div class="flex-grow-1">
                    <div class="au-shortcut-title">{{ $t("nav.clients") }}</div>
                    <div class="au-shortcut-text">{{ $t("dashboard.goClients") }}</div>
                </div>
                <i class="bi bi-arrow-right"></i>
            </router-link>
            <router-link v-if="isSuperAdmin" class="au-card au-shortcut" :to="{ name: 'Distributors' }">
                <div class="au-tile is-cyan"><i class="bi bi-shop"></i></div>
                <div class="flex-grow-1">
                    <div class="au-shortcut-title">{{ $t("nav.distributors") }}</div>
                    <div class="au-shortcut-text">{{ $t("dashboard.goDistributors") }}</div>
                </div>
                <i class="bi bi-arrow-right"></i>
            </router-link>
            <router-link v-if="isSuperAdmin" class="au-card au-shortcut" :to="{ name: 'Users' }">
                <div class="au-tile is-rose"><i class="bi bi-people-fill"></i></div>
                <div class="flex-grow-1">
                    <div class="au-shortcut-title">{{ $t("nav.users") }}</div>
                    <div class="au-shortcut-text">{{ $t("dashboard.goUsers") }}</div>
                </div>
                <i class="bi bi-arrow-right"></i>
            </router-link>
        </div>
    </div>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent } from "vue";
import { useAuthStore } from "@/stores/auth.store";
import { UserRole } from "@/enums/user-role.enum";
import distributorService from "@/services/distributor.service";
import clientService from "@/services/client.service";
import userService from "@/services/user.service";
import { showToast } from "@/utils/toast";

export default defineComponent({
    name: "Dashboard",
    data() {
        return {
            authStore: useAuthStore(),
            loading: false,
            clientsCount: 0,
            distributorsCount: 0,
            usersCount: 0,
        };
    },
    computed: {
        isSuperAdmin(): boolean {
            return this.authStore.user?.role === UserRole.SuperAdmin;
        },
        firstName(): string {
            return (this.authStore.user?.fullName ?? "").trim().split(/\s+/)[0] ?? "";
        },
        roleName(): string {
            const role = this.authStore.user?.role;
            return role ? t(`roles.${UserRole[role]}`) : "";
        },
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                // Dystrybutorzy są już zawężeni do użytkownika, więc klientów liczymy per dystrybutor (działa dla obu ról)
                const distributors = await distributorService.getDistributors();
                const clientLists = await Promise.all(
                    distributors.map(distributor => clientService.getClientsByDistributor(distributor.id as string)));
                this.distributorsCount = distributors.length;
                this.clientsCount = clientLists.reduce((sum, list) => sum + list.length, 0);
                if (this.isSuperAdmin) {
                    const users = await userService.getUsers();
                    this.usersCount = users.length;
                }
            } catch (error) {
                showToast(t("common.loadFailed"), "error");
                console.error("Error loading dashboard:", error);
            } finally {
                this.loading = false;
            }
        },
    },
    mounted() {
        this.load();
    },
});
</script>
